import { Variants } from 'motion/react';

import { QuadrantDecorator } from '@natu/ui/Decorators/DynamicDecorator';
import { MotionWrapper } from '@natu/ui/Decorators/MotionWrapper';
import { cn } from '@natu/utils/cn';
import { DEFAULT_EASE } from '@natu/utils/consts';

import {
  type BlokItem,
  DynamicRender,
  resolveStoryblokStyles,
  sbEditable,
  type SBProps,
  type Spacing,
} from '../../../../utils';

interface SBDrivenByInnovationSectionProps {
  title?: BlokItem[];
  quadrantItems?: BlokItem[];
  mtMobile?: Spacing;
  mtTablet?: Spacing;
  mtDesktop?: Spacing;
  mbMobile?: Spacing;
  mbTablet?: Spacing;
  mbDesktop?: Spacing;
}

const containerVariants: Variants = {
  hidden: { opacity: 0 },
  whileInView: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      ease: DEFAULT_EASE,
    },
  },
};

const itemVariants: Variants = {
  hidden: { y: 20, opacity: 0 },
  whileInView: {
    opacity: 1,
    y: 0,
    transition: {
      ease: DEFAULT_EASE,
    },
  },
};

export const SBDrivenByInnovationSection = ({
  blok,
}: SBProps<SBDrivenByInnovationSectionProps>) => {
  const { title, quadrantItems, mbMobile, mbTablet, mbDesktop, mtMobile, mtTablet, mtDesktop } =
    blok;

  const className = resolveStoryblokStyles({
    mt: mtMobile,
    mtTablet,
    mtDesktop,
    mb: mbMobile,
    mbTablet,
    mbDesktop,
  });

  return (
    <MotionWrapper
      className={cn(
        className,
        'relative grid w-full justify-items-center xl:grid-cols-[auto_max-content] xl:justify-items-end',
      )}
      variants={containerVariants}
      viewport={{ once: true }}
      initial="hidden"
      whileInView="whileInView"
      {...sbEditable(blok)}
    >
      <MotionWrapper
        className="z-10 row-start-1 xl:absolute xl:left-0 xl:top-0 xl:col-span-1"
        variants={itemVariants}
      >
        <div className="xl:max-w-[520px]">
          <DynamicRender data={title} />
        </div>
      </MotionWrapper>

      {/* Mobile View */}
      <MotionWrapper
        className="grid grid-cols-2 items-center gap-0 py-9 lg:hidden"
        variants={containerVariants}
      >
        <MotionWrapper variants={itemVariants}>
          <DynamicRender data={quadrantItems?.[1]} />
        </MotionWrapper>
        <MotionWrapper variants={itemVariants}>
          <DynamicRender data={quadrantItems?.[2]} />
        </MotionWrapper>
        <MotionWrapper variants={itemVariants}>
          <DynamicRender data={quadrantItems?.[4]} />
        </MotionWrapper>
        <MotionWrapper variants={itemVariants} className="grid justify-end">
          <QuadrantDecorator className="text-primary-700 h-[38px] w-[38px] -rotate-90 md:h-[68px] md:w-[68px]" />
        </MotionWrapper>
        <MotionWrapper variants={itemVariants}>
          <DynamicRender data={quadrantItems?.[3]} />
        </MotionWrapper>
        <MotionWrapper variants={itemVariants}>
          <DynamicRender data={quadrantItems?.[0]} />
        </MotionWrapper>
      </MotionWrapper>

      {/* Desktop View */}
      <MotionWrapper
        className="relative col-span-2 hidden grid-cols-[repeat(4,_minmax(0,_204px))] grid-rows-[repeat(2,_minmax(0,_204px))] lg:grid xl:row-start-1 xl:-ml-[216px]"
        variants={containerVariants}
      >
        <MotionWrapper
          variants={itemVariants}
          className="text-naturaily-butterscotch absolute -left-[88px] bottom-0 h-[38px] w-[38px]"
        >
          <QuadrantDecorator />
        </MotionWrapper>

        <MotionWrapper
          variants={itemVariants}
          className="col-span-2 grid origin-bottom justify-end"
        >
          <DynamicRender data={quadrantItems?.[0]} />
        </MotionWrapper>
        <MotionWrapper className="origin-right" variants={itemVariants}>
          <DynamicRender data={quadrantItems?.[1]} />
        </MotionWrapper>
        <MotionWrapper className="origin-bottom" variants={itemVariants}>
          <DynamicRender data={quadrantItems?.[2]} />
        </MotionWrapper>
        <MotionWrapper variants={itemVariants} className="col-span-2 grid justify-start">
          <DynamicRender data={quadrantItems?.[3]} />
        </MotionWrapper>
        <MotionWrapper variants={itemVariants}>
          <DynamicRender data={quadrantItems?.[4]} />
        </MotionWrapper>
        <MotionWrapper variants={itemVariants} className="grid items-end justify-end">
          <QuadrantDecorator className="text-primary-700 h-[68px] w-[68px] -rotate-90" />
        </MotionWrapper>
      </MotionWrapper>
    </MotionWrapper>
  );
};
